import { useToast } from "@/components/ui";
import { useUserContext } from "@/context/AuthContext";
import { getBackendCurrentUser } from "@/lib/backend/api";
import { isUserBanned } from "@/lib/appwrite/userViolationAPI";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { buildSignInRedirect } from "@/lib/auth/redirect";

export const AuthCallback = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated } = useUserContext();
  const isProcessing = useRef(false); // Use a ref to track processing state

  useEffect(() => {
    const handleAuthCallback = async () => {
      // If already processing, return early
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        const accountUser = await getBackendCurrentUser();
        if (!accountUser) throw new Error("No account data received");
        const userId = String(accountUser.id ?? accountUser.$id);
        const userIsBanned = await isUserBanned(userId);
        if (userIsBanned) {
          toast({
            title: "Account Suspended",
            description: "Your account is under ban due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
            variant: "destructive"
          });
          navigate("/sign-in?banned=true");
          return;
        }

        toast({
          title: "Signed in",
          description: "Welcome back to Busiconn.",
        });
        setIsAuthenticated(true);
        setUser({
          $id: userId,
          id: userId,
          name: accountUser.name ?? "",
          username: accountUser.username ?? accountUser.name ?? "",
          emailOrPhone: accountUser.emailOrPhone ?? accountUser.email ?? "",
          imageUrl: accountUser.imageUrl ?? "",
          bio: accountUser.bio ?? "",
          acceptedSLA: Boolean(accountUser.acceptedSla ?? accountUser.acceptedSLA),
        } as any);
        navigate('/');

      } catch (error) {
        console.error("Auth callback error:", error);
        toast({
          title: "Authentication failed",
          description: "Please sign in again",
          variant: "destructive",
        });
        navigate(buildSignInRedirect("/home"));
      } finally {
        isProcessing.current = false; // Reset flag after processing
      }
    };

    handleAuthCallback();
  }, [toast, navigate]); // Ensure dependencies are correct

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">Processing your signup/signin...</h2>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
      </div>
    </div>
  );
};
